import { useState } from 'react';

export interface RallyBucketDatum {
  label: string;
  played: number;
  won: number;
}

interface RallyBucketChartProps {
  buckets: RallyBucketDatum[];
}

const WIDTH = 480;
const HEIGHT = 200;
const PADDING = { top: 16, right: 16, bottom: 36, left: 16 };

/**
 * Bar chart of points played per rally-length bucket, with each bar's win %
 * as its label. No precedent component for this shape in the codebase, so
 * it follows AttendanceTrendChart.tsx's conventions where they apply:
 * recessive hairline gridlines, accessible table fallback, no new dependency.
 */
export function RallyBucketChart({ buckets }: RallyBucketChartProps) {
  const [showTable, setShowTable] = useState(false);

  const totalPlayed = buckets.reduce((sum, b) => sum + b.played, 0);
  if (totalPlayed === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Rally Profile</h3>
        <p className="text-sm text-gray-500">No rally-length data tagged yet.</p>
      </div>
    );
  }

  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const maxPlayed = Math.max(...buckets.map((b) => b.played), 1);
  const barWidth = plotWidth / buckets.length - 16;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Rally Profile</h3>
          <p className="text-sm text-gray-500 mt-0.5">Points played and won % by rally length</p>
        </div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="text-sm text-primary hover:underline"
          aria-label={showTable ? 'Show rally profile as chart' : 'Show rally profile as table'}
        >
          {showTable ? 'Show chart' : 'View as table'}
        </button>
      </div>

      {showTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4 font-medium">Rally length</th>
                <th className="py-2 pr-4 font-medium">Points played</th>
                <th className="py-2 font-medium">Win %</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.label} className="border-b border-gray-100">
                  <td className="py-2 pr-4 text-gray-900">{b.label}</td>
                  <td className="py-2 pr-4 text-gray-600">{b.played}</td>
                  <td className="py-2 font-medium text-gray-900">{b.played > 0 ? Math.round((b.won / b.played) * 100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-auto" role="img" aria-label="Bar chart of points played and won percentage by rally length">
          {[0, 0.5, 1].map((frac) => {
            const y = PADDING.top + plotHeight - frac * plotHeight;
            return <line key={frac} x1={PADDING.left} y1={y} x2={WIDTH - PADDING.right} y2={y} stroke="#e7e5e4" strokeWidth={1} />;
          })}

          {buckets.map((b, i) => {
            const slot = plotWidth / buckets.length;
            const x = PADDING.left + i * slot + (slot - barWidth) / 2;
            const barHeight = (b.played / maxPlayed) * plotHeight;
            const y = PADDING.top + plotHeight - barHeight;
            const winPct = b.played > 0 ? Math.round((b.won / b.played) * 100) : 0;
            return (
              <g key={b.label}>
                <rect x={x} y={y} width={barWidth} height={barHeight} rx={4} fill="#2e8b57" fillOpacity={0.85} />
                <text x={x + barWidth / 2} y={y - 6} textAnchor="middle" className="fill-stone-700" fontSize={11} fontWeight={600}>
                  {winPct}%
                </text>
                <text x={x + barWidth / 2} y={PADDING.top + plotHeight + 16} textAnchor="middle" className="fill-stone-500" fontSize={10}>
                  {b.label}
                </text>
                <text x={x + barWidth / 2} y={PADDING.top + plotHeight + 28} textAnchor="middle" className="fill-stone-400" fontSize={9}>
                  {b.played} pts
                </text>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

export default RallyBucketChart;
